import axios from 'axios';
import { PORTAL } from './portal';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 on a game API means the session/token expired → clear and bounce to
    // login. Auth endpoints (login, change-password) return 401 for wrong
    // credentials and are handled by their callers, so don't hijack those.
    const url: string = error.config?.url ?? '';
    if (error.response?.status === 401 && !url.includes('/auth/')) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_source');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authApi = {
  devLogin: (username: string, role: string, password?: string) =>
    api.post('/auth/dev-login', { username, role, password, source: PORTAL }),
  changePassword: (newPassword: string, currentPassword?: string) =>
    api.post('/auth/change-password', { newPassword, currentPassword }),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/user'),
  getConfig: () => api.get('/auth/config'),
  // CrazyGames account login: token verified server-side; optional guest device id to
  // migrate an in-progress guest's progress into the linked account.
  crazyGamesLogin: (token: string, guestDeviceId?: string) =>
    api.post('/auth/crazygames-login', { token, guestDeviceId }),
  crazyGamesGuest: (deviceId: string) =>
    api.post('/auth/crazygames-guest', { deviceId }),
};

export const gameApi = {
  getPlayer: () => api.get('/game/player'),
  getMissions: () => api.get('/game/missions'),
  getActiveMissions: () => api.get('/game/missions/active'),
  getMissionCompletions: () => api.get('/game/mission-completions'),
  acceptMission: (missionId: string) => api.post(`/game/missions/${missionId}/accept`),
  completeMission: (instanceId: string, viaAds = false) =>
    api.post(`/game/missions/instances/${instanceId}/complete`, null, { params: viaAds ? { viaAds: true } : {} }),
  cancelMission: (instanceId: string) => api.delete(`/game/missions/instances/${instanceId}`),
  getLeaderboard: (page?: number) =>
    api.get('/game/leaderboard', { params: page ? { page } : {} }),
  getHackLog: () => api.get('/game/hacklog'),
  getHackedNodes: () => api.get('/game/hacked-nodes'),
  getInfections: () => api.get('/game/infections'),
  collectInfections: (viaAds = false) => api.post('/game/infections/collect', null, { params: viaAds ? { viaAds: true } : {} }),
  getInfectionsPending: () => api.get('/game/infections/pending'),
  hdBackup:  (kind: 'software' | 'file', key: string) => api.post('/game/encryptedhd/backup',  { kind, key }),
  hdRestore: (kind: 'software' | 'file', key: string) => api.post('/game/encryptedhd/restore', { kind, key }),
};

export const storeApi = {
  getCatalog: () => api.get('/store'),
  buyItem: (itemId: string, serverId?: string) =>
    api.post('/store/buy', { itemId, serverId: serverId ?? null }),
};

export const adminApi = {
  getUsers: (params: { page: number; size: number; search?: string; sort?: string; dir?: string }) =>
    api.get('/admin/users', { params }),
  updateRole: (userId: string, role: string) =>
    api.patch(`/admin/users/${userId}/role`, { role }),
  banUser: (userId: string, reason: string, durationHours?: number) =>
    api.post(`/admin/users/${userId}/ban`, { reason, durationHours: durationHours ?? null }),
  unbanUser: (userId: string) => api.delete(`/admin/users/${userId}/ban`),
  setWhitelist: (userId: string, whitelisted: boolean) =>
    api.post(`/admin/users/${userId}/whitelist`, { whitelisted }),
  getLastActivity: () => api.get('/admin/activity/last'),
  getUserActivity: (userId: string) => api.get(`/admin/users/${userId}/activity`),
  getUserEarnings: (userId: string) => api.get(`/admin/users/${userId}/earnings`),
  getInfectionStats: () => api.get('/admin/infections/stats'),
  getSoftwareStats: () => api.get('/admin/software/stats'),
};

export const hackApi = {
  scan: (targetIp: string) =>
    api.post('/hack/scan', { targetIp }),
  attack: (targetIp: string, attackType: string) =>
    api.post('/hack/attack', { targetIp, attackType }),
};

export const softwareApi = {
  run:            (softwareId: string) => api.post(`/software/${softwareId}/run`),
  stop:           (softwareId: string) => api.post(`/software/${softwareId}/stop`),
  upgrade:        (softwareId: string) => api.post(`/software/${softwareId}/upgrade`),
  confirmInstall: (softwareId: string) => api.post(`/software/${softwareId}/confirm-install`),
  cancel:         (softwareId: string) => api.delete(`/software/${softwareId}`),
};

export const fileSystemApi = {
  connect:          (targetIp: string) =>
    api.post('/filesystem/connect', { targetIp }),
  listFiles:        (targetIp: string) =>
    api.get('/filesystem/files', { params: { targetIp } }),
  readFile:         (targetIp: string, fileId: string) =>
    api.post('/filesystem/read', { targetIp, fileId }),
  download:         (targetIp: string, fileId: string) =>
    api.post('/filesystem/download', { targetIp, fileId }),
  completeDownload: (fileName: string, content: string, sizeMb: number, pendingId?: string) =>
    api.post('/filesystem/complete-download', { fileName, content, sizeMb, pendingId }),
  cancelDownload:   (pendingId: string) =>
    api.post('/filesystem/cancel-download', { pendingId }),
  deleteFile:       (fileId: string, targetIp?: string) =>
    api.delete(`/filesystem/files/${fileId}`, { params: targetIp ? { targetIp } : {} }),
  localRead:        (fileId: string) =>
    api.get('/filesystem/local-read', { params: { fileId } }),
  listSoftware:     (targetIp: string) =>
    api.get('/filesystem/software', { params: { targetIp } }),
  stopSoftware:     (targetIp: string, softwareId: string) =>
    api.post('/filesystem/software/stop', { targetIp, softwareId }),
  deleteSoftware:   (targetIp: string, softwareId: string) =>
    api.post('/filesystem/software/delete', { targetIp, softwareId }),
  downloadSoftware: (targetIp: string, softwareId: string) =>
    api.post('/filesystem/software/download', { targetIp, softwareId }),
  infect:           (targetIp: string) =>
    api.post('/filesystem/infect', { targetIp }),
};

export const ipChangeApi = {
  initiate: (userId: string) => api.post(`/players/${userId}/ip-change/initiate`),
};

export const formatApi = {
  initiate: (userId: string) => api.post(`/players/${userId}/format/initiate`),
};
